<template>
  <div class="channel-page">
    <Head title="Channel" />

    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Operations</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Channel</h1>
        <p class="mt-1.5 text-sm text-gray-500">
          Configure acquisition channels and monitor linked campaigns.
        </p>
      </div>
      <Link
        v-if="!isAgency"
        href="/channel/create"
        class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
      >
        Create Channel
      </Link>
    </div>

    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total channels</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ formatNumber(channels.total || 0) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">On this page</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ channels.data.length }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Active shown</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ activeCount }}</p>
      </div>
    </div>

    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h2 class="text-sm font-bold text-brand-900">Filters</h2>
        <p class="text-xs text-gray-500">Search channels by name or code</p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="md:col-span-2">
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.search"
            type="text"
            placeholder="Search by name or code..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Sort by</label>
          <select
            v-model="form.orderBy"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="created_at_desc">Newest first</option>
            <option value="created_at_asc">Oldest first</option>
            <option value="name_asc">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
            <option value="agtCd_asc">Code ascending</option>
            <option value="agtCd_desc">Code descending</option>
          </select>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          type="button"
          class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          @click="reset"
        >
          Clear filters
        </button>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="border-b border-gray-100 px-5 py-3">
        <h2 class="text-sm font-bold text-brand-900">Channel list</h2>
        <p class="text-xs text-gray-500">
          Showing {{ channels.data.length }} of {{ formatNumber(channels.total || 0) }}
        </p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Code</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Name</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Status</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Campaigns</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Created</th>
              <th class="relative px-4 py-3"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="channel in channels.data"
              :key="channel.agtCd"
              class="border-b border-gray-50 transition-colors hover:bg-brand-50/40"
            >
              <td class="px-4 py-3.5">
                <Link
                  :href="`/channel/${channel.agtCd}/edit`"
                  class="font-mono text-sm font-semibold text-brand-700 hover:text-brand-900"
                >
                  {{ channel.agtCd }}
                </Link>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm font-medium text-gray-800">{{ channel.name }}</td>
              <td class="whitespace-nowrap px-4 py-3.5">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="channel.status === 'Y' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'"
                >
                  {{ channel.status === 'Y' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm font-semibold text-brand-800">{{ channel.count }}</td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-500">{{ formatDate(channel.created_at) }}</td>
              <td class="px-4 py-3.5 text-right">
                <Link
                  :href="`/channel/${channel.agtCd}/edit`"
                  class="inline-flex items-center rounded-lg p-1.5 text-gray-400 transition hover:bg-brand-50 hover:text-brand-700"
                  title="Edit"
                >
                  <icon name="cheveron-right" class="h-5 w-5" />
                </Link>
              </td>
            </tr>
            <tr v-if="!channels.data.length">
              <td colspan="6" class="px-4 py-16 text-center">
                <p class="text-base font-bold text-brand-900">No channels found</p>
                <p class="mt-1 text-sm text-gray-500">Try adjusting your search or create a new channel.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
        <pagination class="mt-0" :links="channels.links || []" preserve-scroll />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import moment from 'moment'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    channels: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search || '',
        orderBy: this.filters.orderBy || 'created_at_desc',
      },
    }
  },
  computed: {
    isAgency() {
      return this.$page.props.auth.user?.role === 'agency'
    },
    activeCount() {
      return (this.channels.data || []).filter((c) => c.status === 'Y').length
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        const payload = pickBy(this.form, (v) => v !== '' && v != null)
        this.$inertia.get('/channel', payload, { preserveState: true })
      }, 300),
    },
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString('id-ID')
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YY') : '—'
    },
    reset() {
      this.form = mapValues(this.form, () => '')
      this.form.orderBy = 'created_at_desc'
    },
  },
}
</script>
